import { toScaled } from './money.ts';

/**
 * Invoice screen maths (2026-10-16): the two-way Bill amount / Dispatcher amount of a line, its rounding, and the exchange-rate lookup.
 * Everything is BigInt decimal arithmetic on decimal strings, never floats.
 *
 * The rate means: 1 unit of the BILL currency = `rate` units of the DISPATCHER currency (OMR to INR 246.63: 1 OMR = 246.63 INR).
 *   Dispatcher amount = Bill amount x Rate        Bill amount = Dispatcher amount / Rate
 * This is the same relation the database stores (order_items.total = round(amount / rate, 3): `amount` is the Dispatcher amount, `total` the Bill amount).
 */

/** Decimals an amount shows and rounds to: 3 for OMR, 2 for every other currency. */
export const decimalsFor = (currencyCode: string | null | undefined): number => (currencyCode?.toUpperCase() === 'OMR' ? 3 : 2);

/** The rate is held with up to 8 decimals (the database column's precision). */
export const RATE_SCALE = 8;

const pow10 = (n: number) => 10n ** BigInt(n);

const fromScaled = (n: bigint, scale: number): string => {
  const neg = n < 0n;
  const abs = neg ? -n : n;
  const s = abs.toString().padStart(scale + 1, '0');
  const int = s.slice(0, s.length - scale);
  return `${neg ? '-' : ''}${int}${scale ? `.${s.slice(s.length - scale)}` : ''}`;
};

/** Rounds a non-negative decimal string half-up to `dp` decimals ("1.005" to 2 -> "1.01"). Null if it is not a plain decimal. */
export function roundDecimal(value: string | null | undefined, dp: number): string | null {
  if (value === null || value === undefined) return null;
  const m = /^(\d+)(?:\.(\d+))?$/.exec(String(value).trim().replace(/,/g, ''));
  if (!m) return null;
  const frac = m[2] ?? '';
  if (frac.length <= dp) return fromScaled(BigInt(m[1]! + frac.padEnd(dp, '0')), dp);
  const kept = BigInt(m[1]! + frac.slice(0, dp).padEnd(dp, '0'));
  return fromScaled(frac.charCodeAt(dp) >= 53 ? kept + 1n : kept, dp); // '5' is char code 53: the next digit decides, half-up
}

/** A positive decimal (an amount or a rate) with up to `maxDp` decimals, as a scaled BigInt; null if empty, malformed, too precise or not above zero. */
function positive(value: string | null | undefined, maxDp: number): { n: bigint; scale: number } | null {
  const n = toScaled(value, maxDp);
  return n !== null && n > 0n ? { n, scale: maxDp } : null;
}

/** Dispatcher amount = Bill amount x Rate, rounded half-up to `dp` decimals. Null when either is empty or not above zero. */
export function dispatcherFromBill(bill: string | null | undefined, rate: string | null | undefined, dp: number): string | null {
  const b = positive(bill, 6);
  const r = positive(rate, RATE_SCALE);
  if (!b || !r) return null;
  // (b / 10^6) x (r / 10^8) = b*r / 10^14 units = b*r*10^dp / 10^14 in the smallest unit
  const num = b.n * r.n * pow10(dp);
  const den = pow10(b.scale + r.scale);
  return fromScaled((num * 2n + den) / (2n * den), dp);
}

/** Bill amount = Dispatcher amount / Rate, rounded half-up to `dp` decimals. Null when either is empty or not above zero. */
export function billFromDispatcher(dispatcher: string | null | undefined, rate: string | null | undefined, dp: number): string | null {
  const d = positive(dispatcher, 6);
  const r = positive(rate, RATE_SCALE);
  if (!d || !r) return null;
  // (d / 10^6) / (r / 10^8) = d*10^2 / r units = d*10^(2+dp) / r in the smallest unit
  const num = d.n * pow10(RATE_SCALE - d.scale + dp);
  return fromScaled((num * 2n + r.n) / (2n * r.n), dp);
}

/** Shows a decimal string with exactly `dp` decimals (rounding half-up); anything that is not a number is returned unchanged. */
export const toFixedDecimal = (value: string | null | undefined, dp: number): string => roundDecimal(value, dp) ?? String(value ?? '');

/** 1 / rate to 8 decimals (the rate of the opposite direction when only one direction is in the table). Null for an invalid rate. */
export function inverseRate(rate: string | null | undefined): string | null {
  const r = positive(rate, RATE_SCALE);
  if (!r) return null;
  const num = pow10(RATE_SCALE + RATE_SCALE);
  return fromScaled((num * 2n + r.n) / (2n * r.n), RATE_SCALE);
}

/** Do two rates have the same value (ignoring trailing zeros)? */
export function sameRate(a: string | null | undefined, b: string | null | undefined): boolean {
  const x = toScaled(a, RATE_SCALE);
  const y = toScaled(b, RATE_SCALE);
  return x !== null && y !== null && x === y;
}

/** How far `rate` is from `tableRate`, in percent of the table rate (always >= 0, 4 decimals). Null if either is invalid. */
export function rateDifferencePercent(rate: string | null | undefined, tableRate: string | null | undefined): number | null {
  const x = toScaled(rate, RATE_SCALE);
  const t = toScaled(tableRate, RATE_SCALE);
  if (x === null || t === null || t <= 0n) return null;
  const diff = x > t ? x - t : t - x;
  return Number((diff * 1_000_000n) / t) / 10_000;
}

/**
 * One row of the exchange rate table: `currencyId` against the base currency, `rate1` = units of that currency per 1 unit of the BASE currency
 * (the retail rate). With OMR as the base, INR 251.31 means 1 OMR = 251.31 INR.
 */
export interface TableRate { currencyId: number; rate1: string | null | undefined }

/**
 * The table rate for the pair, in the direction Bill currency -> Dispatcher currency (1 Bill unit = rate Dispatcher units), or null when the table has none.
 *   - same currency: 1
 *   - Bill currency = the base currency: the table's own rate for the Dispatcher currency (retail, `rate1`): 1 OMR = 251.31 INR
 *   - Dispatcher currency = the base currency (the opposite direction): the table only holds currency per base, so 1 / the Bill currency's rate
 *   - two non-base currencies: through the base currency, Dispatcher rate / Bill rate (INR to AED = rate(AED) / rate(INR))
 *   - a currency with no rate in the table has no pair rate
 * The base currency is the one flagged on the Currency table. The table is read in the requested direction first; the inverse / cross is only used when
 * that direction is not in the table.
 */
export function lookupTableRate(
  rates: readonly TableRate[] | null | undefined,
  baseCurrencyId: number | null | undefined,
  billCurrencyId: number | null | undefined,
  dispatcherCurrencyId: number | null | undefined,
): string | null {
  if (!billCurrencyId || !dispatcherCurrencyId) return null;
  if (billCurrencyId === dispatcherCurrencyId) return '1';
  if (!baseCurrencyId || !rates) return null;
  const forCurrency = (id: number) => rates.find((r) => r.currencyId === id)?.rate1 ?? null;
  if (billCurrencyId === baseCurrencyId) {
    const r = forCurrency(dispatcherCurrencyId);
    return positive(r, RATE_SCALE) ? String(r) : null;
  }
  if (dispatcherCurrencyId === baseCurrencyId) return inverseRate(forCurrency(billCurrencyId));
  const billRate = positive(forCurrency(billCurrencyId), RATE_SCALE);
  const dispRate = positive(forCurrency(dispatcherCurrencyId), RATE_SCALE);
  if (!billRate || !dispRate) return null;
  // 1 Bill = (1 / billRate) base = dispRate / billRate Dispatcher units, held to 8 decimals, rounded half-up
  return fromScaled((dispRate.n * pow10(RATE_SCALE) * 2n + billRate.n) / (2n * billRate.n), RATE_SCALE);
}
